import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * KpiCard — a single dashboard KPI tile.
 *
 * Spec strictly enforced:
 *   • Small line icon
 *   • Short title (upper-case eyebrow)
 *   • Large numeric value
 *   • One concise supporting line ("hint")
 *   • Optional click-through arrow
 * Do NOT add mini-charts, long descriptions or decorative gradients.
 */
export function KpiCard({
  label,
  value,
  hint,
  Icon,
  tone = "steel",
  to,
  onClick,
  testId,
  emphasise = false,
}) {
  const toneMap = {
    steel: { chip: "bg-[#a8dadc]/40 text-[#457b9d]", bg: "bg-white" },
    red: { chip: "bg-[#e63946]/10 text-[#e63946]", bg: "bg-white" },
    orange: { chip: "bg-[#F79009]/10 text-[#F79009]", bg: "bg-white" },
    green: { chip: "bg-[#12B76A]/10 text-[#12B76A]", bg: "bg-[#F1FAEE]" }, // honeydew for positive
    blue: { chip: "bg-[#457b9d]/15 text-[#457b9d]", bg: "bg-white" },
    frosted: { chip: "bg-[#a8dadc]/60 text-[#1d3557]", bg: "bg-[#a8dadc]/20" }, // informational
  };
  const t = toneMap[tone] || toneMap.steel;
  const Body = (
    <div
      data-testid={testId}
      className={`${t.bg} rounded-xl border border-[#E4E7EC] p-4 hover:border-[#457b9d] hover:shadow-sm transition-all cursor-pointer h-full ${
        emphasise ? "ring-1 ring-[#a8dadc]" : ""
      }`}
    >
      <div className="flex items-start justify-between mb-2.5">
        <div className={`w-9 h-9 rounded-lg grid place-items-center ${t.chip}`}>
          <Icon size={17} strokeWidth={2} />
        </div>
        {(to || onClick) && <ChevronRight size={14} className="text-[#a8dadc]" />}
      </div>
      <div className="text-[11px] uppercase tracking-[0.08em] text-[#667085] font-semibold">
        {label}
      </div>
      <div className="text-[26px] md:text-[30px] font-bold text-[#1d3557] leading-tight mt-1 font-mono tabular-nums">
        {value}
      </div>
      {hint && (
        <div className="text-[11px] text-[#667085] mt-1 truncate" title={typeof hint === "string" ? hint : undefined}>
          {hint}
        </div>
      )}
    </div>
  );
  if (to) return <Link to={to}>{Body}</Link>;
  if (onClick)
    return (
      <button type="button" onClick={onClick} className="block w-full text-left">
        {Body}
      </button>
    );
  return Body;
}

export function KpiCardSkeleton({ testId }) {
  return (
    <div className="bg-white rounded-xl border border-[#E4E7EC] p-4 h-full animate-pulse" data-testid={testId}>
      <div className="w-9 h-9 rounded-lg bg-[#F1FAEE] mb-3" />
      <div className="w-24 h-2.5 bg-[#E4E7EC] rounded mb-2" />
      <div className="w-16 h-6 bg-[#E4E7EC] rounded mb-1" />
      <div className="w-20 h-2 bg-[#E4E7EC] rounded" />
    </div>
  );
}
